/**
 * MarqueeSurface — surface de tracé de la sélection de zone, montée sur le
 * calque image sélectionné quand l'outil de zone est actif.
 *
 * CE QUI NE PASSE PAS PAR REACT
 * Le contour vit dans deux nœuds <path> dont on réécrit l'attribut `d`
 * directement pendant le geste. Une souris émet jusqu'à 120 événements par
 * seconde : y répondre par un `setState` demanderait 120 rendus React pour
 * déplacer un pointillé. Même discipline que la gomme (cf. EraserSurface), et
 * même cadence sur `requestAnimationFrame`.
 *
 * Seul le lasso POLYGONAL écrit dans le store en cours de tracé, parce que ses
 * sommets se posent au clic — quelques écritures par tracé. Ce détour a une
 * raison : le gestionnaire de clavier unique de LabelCanvas doit savoir qu'un
 * polygone est en cours pour affecter Échap et Entrée au tracé plutôt qu'à
 * l'outil.
 *
 * LA CONTRAINTE MAJ SE LIT SUR L'ÉVÉNEMENT, JAMAIS DANS UN ÉTAT À PART
 * Première version : un `useRef` alimenté par des écouteurs `keydown`/`keyup`
 * sur window. Le banc d'essai a montré la faille — si la fenêtre perd le focus
 * pendant que Maj est enfoncée, le `keyup` n'arrive jamais et TOUS les gestes
 * suivants restent contraints, sans que rien ne l'indique. Or chaque événement
 * pointeur porte déjà `shiftKey`, toujours exact. Deux écouteurs de moins, et
 * plus d’état parallèle à resynchroniser.
 *
 * POURQUOI DEUX CONTOURS SUPERPOSÉS
 * Un trait blanc en pointillé disparaît sur une image claire, un trait noir sur
 * une image sombre. Le classique « marching ants » superpose donc un trait plein
 * sombre et un pointillé clair animé : lisible sur n'importe quel fond.
 *
 * UN PIXEL, ET C'EST LE viewBox QUI S'EN CHARGE
 * Le `viewBox` vaut `W × H` en pixels ÉCRAN, alors que la boîte CSS du <svg>
 * mesure la taille du calque en unités du document. Une unité de `viewBox` vaut
 * donc exactement un pixel à l'écran, à n'importe quel zoom : l'épaisseur comme
 * le pointillé se donnent tels quels.
 *
 * `vector-effect: non-scaling-stroke` faisait précisément l'inverse de ce que son
 * nom promet ici. Il fait ignorer au trait la transformation du `viewBox` — la
 * seule qui le ramenait à un pixel — et laisse s'appliquer le `scale(zoom)` du
 * conteneur : le contour était peint à `1 × zoom` pixels. Mesuré au banc : 2 px
 * au zoom 2, 4 px au zoom 4 (le défaut par défaut), 8 px au zoom 8, pointillé
 * grossi d'autant. Il est retiré.
 */
import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { useLabelStore } from '../../store/useLabelStore';
import {
    coordsBoite,
    brider,
    contraindreCarre,
    contraindre45,
    cheminSVG,
    marqueeUtile,
    aimanterAuxReperes,
    reperesDuDocument,
} from '../../utils/selection';

// Pas minimal entre deux sommets de lasso libre, en pixels écran. Sans ce
// filtre, un tracé lent accumule des milliers de points colinéaires : le
// polygone devient coûteux à remplir et pèse pour rien.
const PAS_LASSO_PX = 1.5;

// Distance sous laquelle un clic sur le premier sommet ferme le polygone.
const FERMETURE_PX = 10;

export default function MarqueeSurface({ el, scale }) {
    const shape = useLabelStore((s) => s.selection.shape);
    const marquee = useLabelStore((s) => s.marquee);
    const draft = useLabelStore((s) => s.marqueeDraft);

    const svgRef = useRef(null);
    const fillRef = useRef(null);
    const antsRef = useRef(null);
    const dragRef = useRef(null);   // { ancre, points } pendant un glissement
    const rafRef = useRef(0);

    const W = Math.max(2, el.width * scale);
    const H = Math.max(2, el.height * scale);

    /** Écrit un chemin dans les deux nœuds, sans repasser par React. */
    function ecrireChemin(d) {
        if (fillRef.current) fillRef.current.setAttribute('d', d);
        if (antsRef.current) antsRef.current.setAttribute('d', d);
    }

    /** Chemin de l'état courant du store (zone validée, ou polygone en cours). */
    function cheminStore() {
        const src = draft && draft.elementId === el.id ? draft
            : marquee && marquee.elementId === el.id ? marquee
            : null;
        return src ? cheminSVG(src, W, H, el) : '';
    }

    // Remise en phase à chaque changement d'état ou de géométrie : c'est ce qui
    // fait suivre le contour quand on zoome, ou quand un undo change le masque.
    useLayoutEffect(() => {
        ecrireChemin(cheminStore());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [marquee, draft, W, H, el.rotation, el.scaleX, el.scaleY]);

    useEffect(() => () => cancelAnimationFrame(rafRef.current), []);

    // Changement de calque cible : la zone de l'ancien calque n'a plus lieu
    // d'être. On ne la voit plus (le contour n'est tracé que sur son propre
    // calque) et elle resterait pourtant armée sous le Suppr.
    useEffect(() => {
        const st = useLabelStore.getState();
        if (st.marquee && st.marquee.elementId !== el.id) st.clearMarquee();
    }, [el.id]);

    /** Point écran → coordonnées normalisées du masque. Le geste peut dépasser
     *  la boîte du calque, et c'est voulu : cf. `brider` dans utils/selection. */
    function pointer(e) {
        const host = svgRef.current;
        if (!host) return null;
        return brider(coordsBoite(el, scale, host.getBoundingClientRect(), e.clientX, e.clientY));
    }

    /**
     * Le même point, AIMANTÉ aux repères — pour les sommets qu'on POSE.
     *
     * Trois garde-fous, tous alignés sur le glissement d'un calque, parce qu'un
     * magnétisme qui ne se désarme pas de la même façon partout est pire que pas
     * de magnétisme : `snapToGuides` du document, la touche Alt qui le suspend le
     * temps d'un geste, et le bridage qui laisse la zone dépasser le calque.
     *
     * Le LASSO LIBRE en est exclu, et l'appeler ici aurait été le bug évident :
     * ses points sont échantillonnés à la cadence de la souris, pas posés. Chaque
     * passage à moins de 5 px d'un repère y aurait aplati la courbe contre le
     * trait, en plein milieu d'un tracé à main levée.
     */
    function pointerAimante(e) {
        const p = pointer(e);
        if (!p) return null;
        const st = useLabelStore.getState();
        if (st.canvas?.snapToGuides === false || e.altKey) return p;
        const { x, y } = reperesDuDocument(st.elements, st.canvas);
        if (!x.length && !y.length) return p;
        return brider(aimanterAuxReperes(p, el, scale, x, y));
    }

    function planifier(d) {
        if (rafRef.current) return;
        rafRef.current = requestAnimationFrame(() => {
            rafRef.current = 0;
            ecrireChemin(d);
        });
    }

    /** Distance écran entre deux points normalisés, en pixels. */
    function distancePx(a, b) {
        return Math.hypot((a.u - b.u) * W, (a.v - b.v) * H);
    }

    const store = () => useLabelStore.getState();

    // ---- Lasso polygonal : un sommet par clic -----------------------------

    function clicPolygone(p, contraindre) {
        const st = store();
        const encours = st.marqueeDraft?.elementId === el.id ? st.marqueeDraft : null;
        const pts = encours?.points || [];

        // Clic sur le premier sommet = fermeture, à condition d'avoir un triangle.
        if (pts.length >= 3) {
            const premier = { u: pts[0][0], v: pts[0][1] };
            if (distancePx(premier, p) <= FERMETURE_PX) {
                fermerPolygone();
                return;
            }
        }

        // Premier sommet : la zone précédente doit tomber TOUT DE SUITE. Sinon
        // elle reste armée sous le Suppr pendant tout le tracé du polygone, et
        // un Suppr réflexe efface une zone que l'utilisateur croyait remplacée.
        if (!pts.length && st.marquee) st.clearMarquee();

        const dernier = pts.length ? { u: pts[pts.length - 1][0], v: pts[pts.length - 1][1] } : null;
        const q = contraindre && dernier ? brider(contraindre45(dernier, p, el)) : p;
        st.setMarqueeDraft({
            elementId: el.id,
            shape: 'polygon',
            points: [...pts, [q.u, q.v]],
        });
    }

    function fermerPolygone() {
        const st = store();
        const d = st.marqueeDraft;
        if (!d || d.elementId !== el.id) return;
        if (marqueeUtile(d)) st.setMarquee(d);
        else st.clearMarquee();
    }

    // ---- Rectangle / ellipse / lasso libre : un glissement ----------------

    const onPointerDown = (e) => {
        if (e.button !== 0) return;
        e.preventDefault();
        e.stopPropagation();
        const p = shape === 'lasso' ? pointer(e) : pointerAimante(e);
        if (!p) return;

        if (shape === 'polygon') {
            clicPolygone(p, e.shiftKey);
            return;
        }

        e.currentTarget.setPointerCapture?.(e.pointerId);
        dragRef.current = { ancre: p, points: [[p.u, p.v]] };
        // Une zone vide dès le premier appui : le contour précédent doit
        // disparaître tout de suite, sans attendre le relâché.
        if (store().marquee) store().clearMarquee();
        ecrireChemin('');
    };

    const onPointerMove = (e) => {
        const d = dragRef.current;

        // Polygone : élastique du dernier sommet posé jusqu'au curseur.
        if (!d) {
            const dr = draft && draft.elementId === el.id ? draft : null;
            if (shape !== 'polygon' || !dr?.points?.length) return;
            // L'élastique montre la position AIMANTÉE : ce qu'on voit est ce que
            // le clic posera.
            const p = pointerAimante(e);
            if (!p) return;
            const pts = dr.points;
            const dernier = { u: pts[pts.length - 1][0], v: pts[pts.length - 1][1] };
            const q = e.shiftKey ? brider(contraindre45(dernier, p, el)) : p;
            planifier(cheminSVG(
                { shape: 'polygon', points: [...pts, [q.u, q.v]] }, W, H, el
            ));
            return;
        }

        e.preventDefault();
        e.stopPropagation();
        const p = shape === 'lasso' ? pointer(e) : pointerAimante(e);
        if (!p) return;

        if (shape === 'lasso') {
            const pts = d.points;
            const dernier = { u: pts[pts.length - 1][0], v: pts[pts.length - 1][1] };
            if (distancePx(dernier, p) < PAS_LASSO_PX) return;
            pts.push([p.u, p.v]);
            planifier(cheminSVG({ shape: 'lasso', points: pts }, W, H, el));
            return;
        }

        const q = e.shiftKey ? brider(contraindreCarre(d.ancre, p, el)) : p;
        d.points = [[d.ancre.u, d.ancre.v], [q.u, q.v]];
        planifier(cheminSVG({ shape, points: d.points }, W, H, el));
    };

    const finGlissement = (e) => {
        const d = dragRef.current;
        if (!d) return;
        e?.stopPropagation();
        dragRef.current = null;
        cancelAnimationFrame(rafRef.current);
        rafRef.current = 0;

        const zone = { elementId: el.id, shape, points: d.points };
        if (marqueeUtile(zone)) store().setMarquee(zone);
        else {
            store().clearMarquee();
            ecrireChemin('');
        }
    };

    // Un relâché hors du calque doit tout de même clore le tracé, sans quoi la
    // zone resterait en suspens jusqu'au geste suivant.
    useEffect(() => {
        const onUp = () => { if (dragRef.current) finGlissement(); };
        window.addEventListener('pointerup', onUp);
        // Le nettoyage validait le tracé en cours en appelant `onUp()`, sous
        // prétexte de « ne rien perdre ». Il passe pourtant APRÈS `setActiveTool`,
        // qui vient de remettre `marquee` à null en quittant l'outil : la zone
        // était donc réarmée dans le dos de l'utilisateur, invisible, et
        // resurgissait au retour sur l'outil — alors qu'un Échap en plein tracé
        // est justement censé l'abandonner. Le relâché hors du calque, lui, reste
        // couvert par l'écouteur ci-dessus, qui s'exécute avant tout démontage.
        return () => {
            window.removeEventListener('pointerup', onUp);
            if (dragRef.current) {
                dragRef.current = null;
                cancelAnimationFrame(rafRef.current);
                rafRef.current = 0;
                ecrireChemin('');
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [el.id, shape]);

    return (
        <svg
            ref={svgRef}
            data-no-export="true"
            viewBox={`0 0 ${W} ${H}`}
            preserveAspectRatio="none"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={finGlissement}
            onDoubleClick={(e) => {
                e.stopPropagation();
                if (shape === 'polygon') fermerPolygone();
            }}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            className="absolute inset-0 w-full h-full"
            style={{ cursor: 'crosshair', touchAction: 'none' }}
        >
            <path
                ref={fillRef}
                d=""
                fill="rgba(255,255,255,0.10)"
                stroke="rgba(0,0,0,0.75)"
                strokeWidth="1"
            />
            <path
                ref={antsRef}
                d=""
                fill="none"
                stroke="rgba(255,255,255,0.95)"
                strokeWidth="1"
                strokeDasharray="4 4"
                className="lf-ants"
            />
        </svg>
    );
}
